<template>
  <div>
    <simple-box :title="$t('benchmarking.title')" class="mb-5">
      <b-row>
        <b-col v-if="scoringProfilesList != null && scoringProfilesList.length > 0">
          <template v-if="successOnAssociate">
            <div class="mb-2">
              <font-awesome-icon icon="check-circle" class="icon-class-on-success" />
              {{ $t('benchmarking.currentAssociatedProfile') }}
            </div>
          </template>

          <template v-else>
            <div class="mb-2">
              <span v-if="associatedProfiles === 0">
                {{ $t('benchmarking.noAssociatedProfileFound') }}
              </span>
              <span v-else>
                {{ $t('benchmarking.currentAssociatedProfile') }}:
                <b> {{ associatedScoringProfile.name }} </b>
              </span>
            </div>
          </template>

          <div class="d-flex">
            <!-- profiles list -->
            <div :class="['flex-grow-1', isNotCompatible || isPartiallyCompatible ? 'mr-2' : 'mr-3']">
              <custom-select
                :text-field="'name'"
                :value-field="'id'"
                :selected="currentProfileId"
                :options="scoringProfilesListOptions"
                :is-loading="isLoadingAvailableProfiles"
                :highlight-option="setHighlightOption"
                :loading-message="$t('common.loadProfilesList')"
                @input="profileAction"
              ></custom-select>
            </div>

            <!-- is not or partially compatible -->
            <div
              v-if="currentProfile && (isNotCompatible || isPartiallyCompatible)"
              :class="[isNotCompatible || isPartiallyCompatible ? 'mr-2' : '']"
            >
              <div class="mt-1">
                <custom-tooltip
                  section="benchmarking"
                  is-font-awesome-icon
                  :font-awesome-icon="getIncompatibleIcon"
                  :tooltip-text="
                    isNotCompatible ? $t('common.profileNotCompatible') : $t('common.profilePartiallyCompatible')
                  "
                  :css-class="[
                    isNotCompatible ? 'text-danger' : isPartiallyCompatible ? 'partially-compatible' : '',
                    'pointer',
                    'small',
                  ]"
                />
              </div>
            </div>

            <!-- action associate -->
            <div>
              <b-button
                size="sm"
                variant="primary"
                :disabled="getAssociatedId === currentProfileId || currentProfileId == null"
                @click="profileAction(currentProfileId, 'onAssociate')"
                >{{ $t('benchmarking.associate') }}
              </b-button>
            </div>

            <!-- remove associate -->
            <div>
              <b-button variant="link" :disabled="getAssociatedId == null" @click="profileAction(null, 'onRemove')">
                <span class="small removeAssociation align-top">
                  {{ $t('benchmarking.removeAssociation') }}
                  <font-awesome-icon class="text-danger small" icon="trash" />
                </span>
              </b-button>
            </div>
          </div>
        </b-col>
      </b-row>
      <b-row class="mt-2">
        <b-col>
          <b-button class="px-0" size="sm" variant="link" @click="onButtonClick">
            {{ $t('productDetails.createNewScoringProfile') }}
          </b-button>
        </b-col>
      </b-row>
    </simple-box>

    <template v-if="loadingProductScore || productScore != null">
      <b-container fluid class="p-0" v-if="loadingProductScore">
        <b-row class="loading-row">
          <b-col>
            <loading :active="loadingProductScore" :is-full-page="false" color="blue" loader="spinner"> </loading>
          </b-col>
        </b-row>
      </b-container>

      <b-container fluid class="p-0" v-else>
        <b-row class="mb-3">
          <b-col>
            <div class="d-flex">
              <div class="flex-grow-1">
                <h5 class="mb-0">{{ selectedProfileName }}</h5>
              </div>
              <div>
                <b-button @click="goToInsights()" size="sm" variant="outline-primary">
                  {{ $t('scoringProfiles.insight.openInsight') }}
                </b-button>
              </div>
            </div>
          </b-col>
        </b-row>

        <simple-box class="mb-5">
          <b-row>
            <b-col>
              <fairmat-product-score
                class="mb-2"
                :lang="$i18n.locale"
                :key="fairmatProductScoreUpdateKey"
                :is-product-in-test="isProductInTest"
                v-model="productScore"
                show-drivers-scores
                show-action-button-link
                :actionButtonLinkClass="['scoring-show-more-info', 'pt-2']"
                @send-back-score-data-event="showProductScoreData"
                @send-back-distribution-data-event="showDistributionScoreData"
                @send-back-score-filters-event="showIncompatibleScoreFilters"
                data-test="fairmat-product-score"
              ></fairmat-product-score>
            </b-col>
          </b-row>
        </simple-box>
      </b-container>

      <custom-modal
        v-if="benchmarkingModalValue != null"
        :id="'benchmarkingDetails'"
        v-model="benchmarkingModalValue"
        @on-click-table-row="onClickTableRow"
      ></custom-modal>

      <incompatible-filters-modal
        v-if="_incompatibleFiltersValue != null"
        :filters-data="_incompatibleFiltersValue"
        has-go-to-profile
        modal-id="incompatibleFilters"
        @go-to-profile="goToProfile"
      />
    </template>

    <template v-else>
      <b-row class="mt-2">
        <b-col>
          <no-content
            :icon-name="'chart-bar'"
            :icon-class="['mb-3', 'fa-3x']"
            :text="$t('scoringProfiles.noShowedProfile')"
          ></no-content>
        </b-col>
      </b-row>
    </template>
  </div>
</template>

<script>
// Benchmarking tab. Pure prop/event component — see ProductDetails.vue and
// InvestmentOptionDetailPanel.vue for the two mount points (product-level and per-investment-option).
import { round } from '../../utils/formatter'
import SimpleBox from '../Lib/SimpleBox'
import NoContent from '../Lib/NoContent'
import CustomSelect from '../Lib/CustomSelect'
import CustomModal from '../Lib/CustomModal'
import CustomTooltip from '../Lib/CustomTooltip'
import IncompatibleFiltersModal from '../ScoringProfiles/IncompatibleFiltersModal.vue'

export default {
  name: 'benchmarking',
  components: {
    SimpleBox,
    NoContent,
    CustomSelect,
    CustomModal,
    CustomTooltip,
    IncompatibleFiltersModal,
  },
  props: {
    scoringProfilesList: Array,
    associatedScoringProfile: Object,
    currentProfileId: Number,
    productScore: Object,
    loadingProductScore: Boolean,
    fairmatProductScoreUpdateKey: Number,
    successOnAssociate: Boolean,
    isProductInTest: Boolean,
  },

  data() {
    return {
      benchmarking_modal_value: null,
      incompatibleFiltersValue: null,
    }
  },

  computed: {
    getAssociatedId() {
      return this.associatedScoringProfile.id
    },
    setHighlightOption() {
      return {
        id: this.getAssociatedId || null,
        label: this.$t('benchmarking.associated'),
      }
    },
    isNotCompatible() {
      return this.currentProfile.compatibilityType === 0
    },
    isPartiallyCompatible() {
      return this.currentProfile.compatibilityType === 2
    },
    selectedProfileName() {
      return this.currentProfile.name
    },
    currentProfile() {
      return this.scoringProfilesList.filter((item) => item.id === this.currentProfileId)[0]
    },
    associatedProfiles() {
      return this.scoringProfilesList.filter((item) => item.id != null && item.isAssociated).length
    },
    compatibilityType() {
      return {
        0: {
          id: 'isNotCompatible',
          tooltip: this.$t('common.profileNotCompatible'),
          icon: this.getIncompatibleIcon,
          class: 'text-danger',
        },
        1: { id: 'isCompatible', tooltip: null, icon: null, class: null },
        2: {
          id: 'isPartiallyCompatible',
          tooltip: this.$t('common.profilePartiallyCompatible'),
          icon: 'exclamation-triangle',
          class: 'partially-compatible',
        },
      }
    },
    scoringProfilesListOptions() {
      if (this.scoringProfilesList == null) return

      let temp = this.scoringProfilesList.map((el) => {
        return {
          ...el,
          icon: 'compatibilityType' in el ? this.getCompatibilityIcon(el.compatibilityType) : null,
          tooltip: 'compatibilityType' in el ? this.compatibilityType[el.compatibilityType].tooltip : null,
          class: 'compatibilityType' in el ? this.compatibilityType[el.compatibilityType].class : null,
        }
      })
      return temp
    },
    isLoadingAvailableProfiles() {
      if (!Array.isArray(this.scoringProfilesListOptions)) return false

      return (
        this.scoringProfilesListOptions.length === 0 ||
        (this.scoringProfilesListOptions.length === 1 && this.scoringProfilesListOptions[0].id == null)
      )
    },
    productScoreModalFields() {
      return [
        { key: 'investmentOptionName', label: this.$t('common.option'), sortable: true },
        { key: 'compatibilityType', label: this.$t('common.compatibility'), sortable: true },
        {
          key: 'totalScore',
          label: this.$t('common.scoring'),
          sortable: true,
          class: ['text-right'],
          formatter: (val) => (typeof val === 'number' && !isNaN(val) ? this.mathRoundPercentage(val) : '-'),
        },
      ]
    },
    distributionModalFields() {
      return [
        { key: 'investmentOptionName', label: this.$t('common.option'), sortable: true },
        {
          key: 'scoreResult',
          label: this.$t('common.scoring'),
          sortable: true,
          class: ['text-right'],
          formatter: (val) => (typeof val === 'number' && !isNaN(val) ? this.mathRoundPercentage(val) : '-'),
        },
      ]
    },
    getIncompatibleIcon() {
      return this.isProductInTest && this.currentProfile.compatibilityType === 0
        ? 'exclamation-circle'
        : 'exclamation-triangle'
    },
    benchmarkingModalValue: {
      get() {
        return this.benchmarking_modal_value
      },
      set(newValue) {
        this.benchmarking_modal_value = newValue
      },
    },
    _incompatibleFiltersValue: {
      get() {
        return this.incompatibleFiltersValue
      },
      set(newValue) {
        this.incompatibleFiltersValue = newValue
      },
    },
  },

  methods: {
    getCompatibilityIcon(compatibilityType) {
      if (compatibilityType === 1) {
        return null
      }

      if (compatibilityType === 2) {
        return 'exclamation-triangle'
      }

      return this.isProductInTest && compatibilityType === 0 ? 'exclamation-circle' : 'exclamation-triangle'
    },

    profileAction(val, mode) {
      const action = mode != null || mode != undefined ? mode : 'onSelect'
      this.$emit('on-change-scoring-profile', {
        id: val !== null && typeof val === 'object' ? val.id : val,
        action: action,
      })
    },

    onButtonClick() {
      this.$emit('on-click-create')
    },

    showProductScoreData(value) {
      this.benchmarkingModalValue = {
        title: this.productScore.productKey.name,
        subtitle: this.selectedProfileName,
        subHeader: {
          number: value.scoreResults.length,
          label: this.$t('common.options'),
        },
        otherInfo: {
          label1:
            value.scoreResults.filter((el) => el.compatibilityType === 1).length +
            ' ' +
            this.$t('common.compatibleOptions') +
            ', ' +
            (value.scoreResults.filter((el) => el.compatibilityType === 0).length +
              ' ' +
              this.$t('common.notCompatibleOptions')),
        },
        placeholder: this.$t('common.searchInvestmentOption'),
        items: this.productScoreItems(value),
        fields: this.productScoreModalFields,
        labelDisplayClass: ['d-inline'],
      }

      this.$nextTick(() => {
        this.$bvModal.show('benchmarkingDetails')
      })
    },

    showDistributionScoreData(value) {
      this.benchmarkingModalValue = {
        title: this.productScore.productKey.name,
        subtitle: this.selectedProfileName,
        subHeader: {
          number: value.elements.length,
          label: this.$t('common.modal.elementsInRange'),
        },
        otherInfo: {
          label1:
            this.$t('common.modal.rangeFilter') +
            ' ' +
            this.mathRoundPercentage(value.min) +
            ' - ' +
            this.mathRoundPercentage(value.max),
        },
        placeholder: this.$t('common.searchInvestmentOption'),
        items: this.distributionScoreItems(value),
        fields: this.distributionModalFields,
        labelDisplayClass: ['d-inline'],
      }

      this.$nextTick(() => {
        this.$bvModal.show('benchmarkingDetails')
      })
    },

    showIncompatibleScoreFilters(value) {
      this._incompatibleFiltersValue = value

      this.$nextTick(() => {
        this.$bvModal.show('incompatibleFilters')
      })
    },

    mathRoundPercentage(value) {
      return Math.round(value * 100) + '%'
    },

    onClickTableRow(value) {
      this.openProductDetails(value, this.currentProfileId)
    },

    productScoreItems(value) {
      if (!value) return []

      const items = value.scoreResults.map((item) => {
        return {
          name: value.productKey.name,
          issuerCode: value.productKey.issuerCode,
          referenceDate: value.productKey.referenceDate,
          investmentOptionName: item.investmentOptionName,
          compatibilityType:
            item.compatibilityType === 1 ? this.$t('common.compatible') : this.$t('common.notCompatible'),
          totalScore: item.totalScore,
        }
      })

      return items
    },

    distributionScoreItems(value) {
      if (!value) return []

      let temp = value.elements.filter((el) => round(el.totalScore, 8) >= round(value.min, 8))

      if (!value.isLastColumns) {
        temp = temp.filter((el) => round(el.totalScore, 8) < round(value.max, 8))
      }

      const items = temp.map((el) => {
        return {
          id: el.productId,
          name: this.productScore.productKey.name,
          investmentOptionName: el.investmentOptionName,
          issuerName: this.productScore.productKey.issuerName,
          issuerCode: this.productScore.productKey.issuerCode,
          referenceDate: this.productScore.productKey.referenceDate,
          scoreResult: el.totalScore,
        }
      })

      return items
    },

    goToProfile() {
      this.$router.push({
        name: 'scoringProfilesEdit',
        params: { id: this.currentProfileId.toString() },
      })
    },

    goToInsights() {
      this.$router.push({
        name: 'scoringProfilesDetails',
        params: { scoringProfileId: this.currentProfileId.toString() },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
// Scoped: no other component references these three classes.
.loading-row {
  height: 400px;
}

.icon-class-on-success {
  color: var(--secondary);
}
.removeAssociation {
  color: var(--primary);
}
</style>
